import React, { ReactNode, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { baseXInPixels } from '../../styles/theme';
import DivButton from '../a11y/DivButton';
import CmsMedia from '../cms/CmsMedia';
import IconText from '../common/IconText';
import EllipsisOverflow from '../common/EllipsisOverflow';
import Spacing from '../common/Spacing';
import H from '../elements/H';
import { CmsAsset } from '../../integrations/contentful/types';
import { MediaGalleryItem } from './MediaGalleryModal';

export interface MediaGallerySidebarProps {
  items: MediaGalleryItem[];
  currentIndex: number;
  onItemClick: (index: number) => void;
}

function getItemPhaseTitle(item?: MediaGalleryItem): string | undefined {
  return item && item.phase && item.phase.title;
}

const bottomOffsetForScroll = '40px';
const MediaGallerySidebarItem: React.FunctionComponent<{
  media: CmsAsset;
  isCurrentItem?: boolean;
}> = ({ media, isCurrentItem }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Wait for all pending rendering to be done.
    // This is necessary for the scroll to work when the media gallery is opened.
    setTimeout(() => {
      if (ref.current && ref.current.scrollIntoView && isCurrentItem) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  }, [isCurrentItem, ref]);
  return (
    <div ref={ref}>
      <CmsMedia
        borderRadius={1}
        borderRadiusColor="#000"
        media={media}
        width={baseXInPixels(17)}
        height={baseXInPixels(17)}
        crop
      />
      <div className="scroll-offset" />
      {/*language=CSS*/}
      <style jsx>{`
        .scroll-offset {
          padding-bottom: ${bottomOffsetForScroll};
          margin-bottom: -${bottomOffsetForScroll};
        }
      `}</style>
    </div>
  );
};

const MediaGallerySidebar: React.FunctionComponent<
  MediaGallerySidebarProps
> = ({ items, currentIndex, onItemClick }) => {
  let activePhaseTitle: string | undefined;
  return (
    <div className="media-gallery-modal-sidebar">
      <Spacing left={3} right={3}>
        {items.reduce((items: ReactNode[], item, index) => {
          const currentItemPhaseTitle = getItemPhaseTitle(item);
          const onClick = () => onItemClick(index);

          if (
            currentItemPhaseTitle &&
            currentItemPhaseTitle !== activePhaseTitle &&
            item.phase
          ) {
            items.push(
              <DivButton onClick={onClick} key={currentItemPhaseTitle + index}>
                <div className="phase-title">
                  <Spacing top={2} bottom={1}>
                    <IconText
                      icon={item.phase.iconName}
                      iconSize={2}
                      iconColor="white"
                    >
                      <H styling="h5" tag="div" color="white">
                        <EllipsisOverflow>
                          {currentItemPhaseTitle}
                        </EllipsisOverflow>
                      </H>
                    </IconText>
                  </Spacing>
                </div>
              </DivButton>
            );
            activePhaseTitle = currentItemPhaseTitle;
          }
          const isCurrentItem = index === currentIndex;
          items.push(
            <DivButton onClick={onClick} key={item.media.sys.id + index}>
              <div
                className={classNames('item', {
                  active: isCurrentItem
                })}
              >
                <Spacing bottom={1}>
                  <MediaGallerySidebarItem
                    media={item.media}
                    isCurrentItem={isCurrentItem}
                  />
                </Spacing>
              </div>
            </DivButton>
          );
          return items;
        }, [])}
        {/*language=CSS*/}
        <style jsx>{`
          .item {
            opacity: 0.5;
          }
          .item:hover,
          .item.active {
            opacity: 1;
          }
          .phase-title {
            /* The scroll offset of individual items overlaps the title.
               We need to set a z-index to prevent clicks from titles going to the items. */
            position: relative;
            z-index: 1;
          }
        `}</style>
      </Spacing>
    </div>
  );
};

export default MediaGallerySidebar;
